import { useState, useRef, useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Car, User, Calendar, Wrench, Upload, CheckCircle, AlertCircle,
  ArrowRight, ArrowLeft, Shield, Info, Camera, Receipt, X, FileCheck, ScanLine,
  Mail, Lock, Search, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const steps = [
  { id: 1, title: "Vehicle Information", icon: Car },
  { id: 2, title: "Policyholder Details", icon: User },
  { id: 3, title: "Accident Information", icon: AlertCircle },
  { id: 4, title: "Review & Submit", icon: CheckCircle },
];

export default function FileClaim() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [authMethod, setAuthMethod] = useState(null); // null = show options, "login", "policy", "guest"
  const [authLoading, setAuthLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [policyForm, setPolicyForm] = useState({ policyNumber: "", lastName: "" });
  const [policyVerified, setPolicyVerified] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [vinVerified, setVinVerified] = useState(false);
  const [vinData, setVinData] = useState(null);
  const [verifyingVin, setVerifyingVin] = useState(false);
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [repairInvoice, setRepairInvoice] = useState(null);
  const [scanningVin, setScanningVin] = useState(false);
  const vinScanInputRef = useRef(null);

  const [formData, setFormData] = useState({
    vin: "", policyholder_name: "", policyholder_email: "", policyholder_phone: "",
    accident_date: "", repair_completion_date: "", state: "", repair_severity: "",
    accident_description: "", dealer_source: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please enter your email and password");
      return;
    }
    setAuthLoading(true);
    // Authenticate and pre-fill user data from account
    setTimeout(() => {
      setAuthLoading(false);
      setAuthMethod("login");
      // Pre-fill email from login
      setFormData(prev => ({ ...prev, policyholder_email: loginForm.email }));
      toast.success("Signed in successfully. Your information has been loaded.");
    }, 800);
  };

  const handlePolicyLookup = async (e) => {
    e.preventDefault();
    if (!policyForm.policyNumber || !policyForm.lastName) {
      toast.error("Please enter your policy number and last name");
      return;
    }
    setAuthLoading(true);
    // Verify policy and fetch associated details
    setTimeout(() => {
      setAuthLoading(false);
      setPolicyVerified(true);
      setAuthMethod("policy");
      // Pre-fill name from policy lookup
      setFormData(prev => ({ ...prev, policyholder_name: policyForm.lastName }));
      toast.success("Policy verified. Your information has been loaded.");
    }, 800);
  };

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "vin") { setVinVerified(false); setVinData(null); }
  };

  const verifyVin = async () => {
    if (formData.vin.length !== 17) { toast.error("VIN must be exactly 17 characters"); return; }
    setVerifyingVin(true);
    try {
      const res = await axios.get(`${API}/vin/${formData.vin}/decode`);
      setVinData(res.data); setVinVerified(true); toast.success("VIN verified successfully!");
    } catch (error) { toast.error("Could not verify VIN. Please check and try again."); }
    finally { setVerifyingVin(false); }
  };

  const handleVinScan = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setScanningVin(true);
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(",")[1]; // Remove data:image/xxx;base64, prefix
        
        try {
          const res = await axios.post(`${API}/vin/scan`, { image_base64: base64Data });
          
          if (res.data.success && res.data.vin) {
            updateForm("vin", res.data.vin);
            toast.success(`VIN detected: ${res.data.vin}`);
            // Auto-verify the detected VIN
            setVerifyingVin(true);
            try {
              const verifyRes = await axios.get(`${API}/vin/${res.data.vin}/decode`);
              setVinData(verifyRes.data); setVinVerified(true); toast.success("VIN verified successfully!");
            } catch { toast.error("Could not verify detected VIN. Please verify manually."); }
            finally { setVerifyingVin(false); }
          } else {
            toast.error(res.data.message || "Could not detect VIN from image");
          }
        } catch (error) {
          toast.error("Failed to scan VIN. Please try again or enter manually.");
        }
        setScanningVin(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setScanningVin(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to process image");
      setScanningVin(false);
    }
    
    // Reset the input
    if (vinScanInputRef.current) vinScanInputRef.current.value = "";
  }, []);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + damagePhotos.length > 10) { toast.error("Maximum 10 photos allowed"); return; }
    const newPhotos = files.map(file => ({ file, preview: URL.createObjectURL(file), name: file.name, size: file.size }));
    setDamagePhotos(prev => [...prev, ...newPhotos]); toast.success(`${files.length} photo(s) added`);
  };

  const removePhoto = (index) => {
    setDamagePhotos(prev => { const updated = [...prev]; URL.revokeObjectURL(updated[index].preview); updated.splice(index, 1); return updated; });
  };

  const handleInvoiceUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { toast.error("Invoice file must be less than 10MB"); return; }
      setRepairInvoice({ file, name: file.name, size: file.size, type: file.type }); toast.success("Invoice uploaded");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.vin.length === 17 && vinVerified;
      case 2: return formData.policyholder_name && formData.policyholder_email;
      case 3: return formData.accident_date && formData.state && formData.repair_severity;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Execute reCAPTCHA verification
      if (executeRecaptcha) {
        try {
          const recaptchaToken = await executeRecaptcha("claim_submit");
          await axios.post(`${API}/recaptcha/verify`, { token: recaptchaToken, action: "claim_submit" });
        } catch (recaptchaError) {
          console.warn("reCAPTCHA verification issue:", recaptchaError);
          // Continue with submission even if reCAPTCHA fails (backend will handle)
        }
      }
      
      const res = await axios.post(`${API}/claims`, {
        vin: formData.vin, policyholder_name: formData.policyholder_name, policyholder_email: formData.policyholder_email,
        accident_date: new Date(formData.accident_date).toISOString(),
        repair_completion_date: formData.repair_completion_date ? new Date(formData.repair_completion_date).toISOString() : null,
        state: formData.state, repair_severity: formData.repair_severity, dealer_source: formData.dealer_source || null,
      });
      setClaimId(res.data.claim_id); setSubmitted(true); toast.success("Claim submitted successfully!"); window.scrollTo(0, 0);
    } catch (error) { toast.error("Failed to submit claim. Please try again."); }
    finally { setSubmitting(false); }
  };

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    const hasDecimals = num % 1 !== 0;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: hasDecimals ? 2 : 0 }).format(num);
  };

  if (submitted) {
    return (
      <div data-testid="claim-submitted-success">
        <div className="rounded-2xl px-8 py-12 text-center mb-8 bg-slate-100 border border-slate-200">
          <div className="w-20 h-20 bg-[#4C98D3]/20 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#4C98D3]" /></div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans'] mb-4 text-slate-900">Claim Submitted Successfully!</h1>
          <p className="text-slate-600 text-lg max-w-xl mx-auto">Your diminished value claim has been received and is being processed.</p>
        </div>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-6 shadow-sm">
            <div className="text-center mb-8">
              <p className="text-sm text-slate-600 uppercase tracking-wide mb-2">Your Claim Number</p>
              <p className="text-4xl font-bold text-slate-900 font-mono">{claimId}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3"><Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-amber-800">Save Your Claim Number</p><p className="text-sm text-amber-700 mt-1">You'll need this to track your claim status. We've also sent a confirmation email to {formData.policyholder_email}.</p></div></div>
            </div>
            <h3 className="font-semibold text-slate-900 mb-4">What Happens Next?</h3>
            <ol className="space-y-4">
              <li className="flex gap-4"><div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">1</div><div><p className="font-medium text-slate-900">VIN Verification</p><p className="text-sm text-slate-600">We'll verify your vehicle information and policy details.</p></div></li>
              <li className="flex gap-4"><div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">2</div><div><p className="font-medium text-slate-900">Diminished Value Calculation</p><p className="text-sm text-slate-600">Our system will calculate your vehicle's diminished value.</p></div></li>
              <li className="flex gap-4"><div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">3</div><div><p className="font-medium text-slate-900">Claim Decision</p><p className="text-sm text-slate-600">You'll receive a decision within 5-7 business days.</p></div></li>
            </ol>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="outline" onClick={() => window.location.href = `/claims/${claimId}`} className="gap-2 border-slate-200">View Claim Details<ArrowRight className="w-4 h-4" /></Button>
            <Button onClick={() => window.location.href = "/"} className="bg-[#2563EB] hover:bg-[#1D4ED8] gap-2">Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="file-claim-page">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 md:px-8 pt-10 pb-12 mb-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-6"><FileText className="w-4 h-4 text-[#2563EB]" /><span className="text-[#2563EB] text-sm font-medium">File a Claim</span></div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">Report an Accident & <span className="text-[#2563EB]">File a Claim</span></h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">Complete the form below to submit your diminished value claim. The process takes about 5 minutes.</p>
        </div>
      </div>

      {/* Auth Selection Screen */}
      {authMethod === null && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2 text-center">How would you like to proceed?</h2>
            <p className="text-slate-600 text-center mb-8">Sign in to your account or verify your policy to pre-fill your information.</p>
            
            <div className="grid gap-4 md:grid-cols-3 mb-8">
              {/* Login Option */}
              <button
                onClick={() => setAuthMethod("showLogin")}
                className="p-6 rounded-xl border-2 border-slate-200 hover:border-[#2563EB] hover:bg-blue-50/50 transition-all text-left group"
                data-testid="auth-login-option"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <User className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Sign In</h3>
                <p className="text-sm text-slate-600">Login to your account to auto-fill your details</p>
              </button>

              {/* Policy Number Option */}
              <button
                onClick={() => setAuthMethod("showPolicy")}
                className="p-6 rounded-xl border-2 border-slate-200 hover:border-[#2563EB] hover:bg-blue-50/50 transition-all text-left group"
                data-testid="auth-policy-option"
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <FileText className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Policy Number</h3>
                <p className="text-sm text-slate-600">Verify your policy to link your claim</p>
              </button>

              {/* Guest Option */}
              <button
                onClick={() => setAuthMethod("guest")}
                className="p-6 rounded-xl border-2 border-slate-200 hover:border-[#2563EB] hover:bg-blue-50/50 transition-all text-left group"
                data-testid="auth-guest-option"
              >
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4 group-hover:bg-slate-200 transition-colors">
                  <Car className="w-6 h-6 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Continue with VIN</h3>
                <p className="text-sm text-slate-600">Start fresh with your vehicle information</p>
              </button>
            </div>

            <p className="text-center text-sm text-slate-600">
              Already have a claim? <Link to="/" className="text-[#2563EB] hover:underline font-medium">Track your claim status</Link>
            </p>
          </div>
        </div>
      )}

      {/* Login Form */}
      {authMethod === "showLogin" && (
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <button onClick={() => setAuthMethod(null)} className="text-slate-600 hover:text-slate-900 text-sm flex items-center gap-1 mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to options
            </button>
            <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-6">Sign In to Your Account</h2>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-slate-700">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="file-claim-login-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="file-claim-login-password"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-xl"
                disabled={authLoading}
                data-testid="file-claim-login-submit"
              >
                {authLoading ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" />Signing In...</span> : <span className="flex items-center justify-center gap-2">Sign In & Continue<ArrowRight className="w-4 h-4" /></span>}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <p className="text-sm text-slate-600">Don't have an account?</p>
              <button onClick={() => setAuthMethod("guest")} className="text-[#2563EB] hover:underline font-medium text-sm mt-1">Continue as guest</button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Number Form */}
      {authMethod === "showPolicy" && (
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <button onClick={() => setAuthMethod(null)} className="text-slate-600 hover:text-slate-900 text-sm flex items-center gap-1 mb-6">
              <ArrowLeft className="w-4 h-4" /> Back to options
            </button>
            <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-6">Verify Your Policy</h2>
            
            <form onSubmit={handlePolicyLookup} className="space-y-4">
              <div>
                <Label htmlFor="policy-number" className="text-slate-700">Policy Number</Label>
                <div className="relative mt-2">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="policy-number"
                    type="text"
                    placeholder="Enter your policy number (e.g., POL-123456)"
                    value={policyForm.policyNumber}
                    onChange={(e) => setPolicyForm({ ...policyForm, policyNumber: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="file-claim-policy-number"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="policy-lastname" className="text-slate-700">Last Name on Policy</Label>
                <div className="relative mt-2">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="policy-lastname"
                    type="text"
                    placeholder="Enter your last name"
                    value={policyForm.lastName}
                    onChange={(e) => setPolicyForm({ ...policyForm, lastName: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="file-claim-policy-lastname"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-xl"
                disabled={authLoading}
                data-testid="file-claim-policy-submit"
              >
                {authLoading ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" />Verifying...</span> : <span className="flex items-center justify-center gap-2">Verify Policy & Continue<ArrowRight className="w-4 h-4" /></span>}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-600 text-center">Find your policy number on your insurance documents or confirmation email.</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Form - only show when auth method is selected */}
      {(authMethod === "login" || authMethod === "policy" || authMethod === "guest") && (
        <>
          {/* Auth Method Badge */}
          <div className="max-w-3xl mx-auto mb-4">
            <div className="flex items-center gap-2 text-sm">
              {authMethod === "login" && (
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-[#2563EB] px-3 py-1 rounded-full">
                  <User className="w-3.5 h-3.5" /> Signed in as {loginForm.email}
                </span>
              )}
              {authMethod === "policy" && (
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">
                  <CheckCircle className="w-3.5 h-3.5" /> Policy {policyForm.policyNumber} verified
                </span>
              )}
              {authMethod === "guest" && (
                <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                  <Car className="w-3.5 h-3.5" /> Continuing as guest
                </span>
              )}
              <button onClick={() => { setAuthMethod(null); setPolicyVerified(false); }} className="text-slate-600 hover:text-slate-900 text-sm underline ml-2">Change</button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="max-w-3xl mx-auto mb-8">
            {/* Mobile: Simple step indicator */}
            <div className="sm:hidden flex items-center justify-center gap-2 mb-4">
              <span className="text-sm font-medium text-[#2563EB]">Step {currentStep}</span>
              <span className="text-sm text-slate-600">of {steps.length}</span>
              <span className="text-sm text-slate-600 ml-2">- {steps[currentStep - 1].title}</span>
            </div>
            
            {/* Desktop: Full step indicators */}
            <div className="hidden sm:flex items-center justify-between mb-4">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isComplete = currentStep > step.id;
                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isComplete ? "bg-emerald-500 text-white" : isActive ? "bg-[#2563EB] text-white ring-4 ring-blue-100" : "bg-slate-100 text-slate-600"}`}>
                        {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span className={`text-xs mt-2 ${isActive ? "text-[#2563EB] font-medium" : "text-slate-600"}`}>{step.title}</span>
                    </div>
                    {idx < steps.length - 1 && <div className={`w-24 h-1 mx-2 rounded ${isComplete ? "bg-emerald-500" : "bg-slate-200"}`} />}
                  </div>
                );
              })}
            </div>
            <Progress value={(currentStep / steps.length) * 100} className="h-2" />
          </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
          
          {/* Step 1 */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-vehicle">
              <div><h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2">Vehicle Information</h2><p className="text-slate-600">Enter your vehicle's VIN to get started.</p></div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="vin" className="text-slate-700">Vehicle Identification Number (VIN) *</Label>
                  <div className="flex gap-2 mt-2">
                    <Input id="vin" value={formData.vin} onChange={(e) => updateForm("vin", e.target.value.toUpperCase())} placeholder="Enter 17-character VIN" maxLength={17} className="font-mono tracking-wide flex-1" data-testid="vin-input" />
                    <input
                      type="file"
                      ref={vinScanInputRef}
                      accept="image/*"
                      capture="environment"
                      onChange={handleVinScan}
                      className="hidden"
                      data-testid="vin-scan-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => vinScanInputRef.current?.click()} 
                      disabled={scanningVin}
                      className="border-[#2563EB] text-[#2563EB] hover:bg-blue-50"
                      data-testid="vin-scan-btn"
                      title="Scan VIN with camera"
                    >
                      {scanningVin ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        <Camera className="w-5 h-5 text-[#2563EB]" />
                      )}
                    </Button>
                    <Button type="button" onClick={verifyVin} disabled={formData.vin.length !== 17 || verifyingVin} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="verify-vin-btn">{verifyingVin ? "Verifying..." : "Verify VIN"}</Button>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{formData.vin.length}/17 characters</p>
                </div>
                {vinVerified && vinData && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /><div className="flex-1"><p className="font-medium text-emerald-800">VIN Verified Successfully</p>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div><p className="text-slate-600">Vehicle</p><p className="font-medium text-slate-900">{vinData.vehicle_profile.year} {vinData.vehicle_profile.make} {vinData.vehicle_profile.model}</p></div>
                      <div><p className="text-slate-600">Trim</p><p className="font-medium text-slate-900">{vinData.vehicle_profile.trim}</p></div>
                      <div><p className="text-slate-600">MSRP</p><p className="font-medium text-slate-900">{formatCurrency(vinData.vehicle_profile.msrp)}</p></div>
                      <div><p className="text-slate-600">Current Market Value</p><p className="font-medium text-[#2563EB]">{formatCurrency(vinData.vehicle_profile.current_market_value)}</p></div>
                    </div></div></div>
                  </div>
                )}
                <div className="bg-slate-50 rounded-xl p-4"><div className="flex gap-3"><Info className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-slate-700">Where to find your VIN</p><p className="text-sm text-slate-600 mt-1">Your VIN can be found on your vehicle registration, insurance card, or on a metal plate visible through the windshield on the driver's side. You can also use the <Camera className="w-4 h-4 inline text-[#2563EB]" /> button to scan it with your camera.</p></div></div></div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-policyholder">
              <div><h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2">Policyholder Details</h2><p className="text-slate-600">Provide your contact information.</p></div>
              <div className="grid gap-4">
                <div><Label htmlFor="name" className="text-slate-700">Full Name *</Label><Input id="name" value={formData.policyholder_name} onChange={(e) => updateForm("policyholder_name", e.target.value)} placeholder="Enter your full name" className="mt-2" data-testid="name-input" /></div>
                <div><Label htmlFor="email" className="text-slate-700">Email Address *</Label><Input id="email" type="email" value={formData.policyholder_email} onChange={(e) => updateForm("policyholder_email", e.target.value)} placeholder="Enter your email address" className="mt-2" data-testid="email-input" /><p className="text-sm text-slate-600 mt-1">We'll send claim updates to this email</p></div>
                <div><Label htmlFor="phone" className="text-slate-700">Phone Number (Optional)</Label><Input id="phone" type="tel" value={formData.policyholder_phone} onChange={(e) => updateForm("policyholder_phone", e.target.value)} placeholder="(555) 555-5555" className="mt-2" data-testid="phone-input" /></div>
              </div>
            </div>
          )}

          {/* Step 3 */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-accident">
              <div><h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2">Accident Information</h2><p className="text-slate-600">Provide details about the accident and repairs.</p></div>
              <div className="grid gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="accident_date" className="text-slate-700">Accident Date *</Label><Input id="accident_date" type="date" value={formData.accident_date} onChange={(e) => updateForm("accident_date", e.target.value)} className="mt-2 w-full max-w-full" data-testid="accident-date-input" /></div>
                  <div><Label htmlFor="repair_date" className="text-slate-700">Repair Completion Date</Label><Input id="repair_date" type="date" value={formData.repair_completion_date} onChange={(e) => updateForm("repair_completion_date", e.target.value)} className="mt-2 w-full max-w-full" data-testid="repair-date-input" /><p className="text-xs text-slate-600 mt-1">Leave blank if repairs are not yet complete</p></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="state" className="text-slate-700">State Where Accident Occurred *</Label><Select value={formData.state} onValueChange={(v) => updateForm("state", v)}><SelectTrigger className="mt-2" data-testid="state-select"><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent>{US_STATES.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label htmlFor="severity" className="text-slate-700">Repair Severity *</Label><Select value={formData.repair_severity} onValueChange={(v) => updateForm("repair_severity", v)}><SelectTrigger className="mt-2" data-testid="severity-select"><SelectValue placeholder="Select severity" /></SelectTrigger><SelectContent><SelectItem value="minor_cosmetic">Minor Cosmetic (scratches, dents)</SelectItem><SelectItem value="moderate_structural">Moderate Structural (panel replacement)</SelectItem><SelectItem value="major_structural">Major Structural (frame damage)</SelectItem></SelectContent></Select></div>
                </div>
                <div><Label htmlFor="dealer" className="text-slate-700">Dealership or Repair Shop (Optional)</Label><Input id="dealer" value={formData.dealer_source} onChange={(e) => updateForm("dealer_source", e.target.value)} placeholder="Enter dealership or repair shop name" className="mt-2" data-testid="dealer-input" /></div>
                <div><Label htmlFor="description" className="text-slate-700">Accident Description (Optional)</Label><Textarea id="description" value={formData.accident_description} onChange={(e) => updateForm("accident_description", e.target.value)} placeholder="Briefly describe what happened..." className="mt-2" rows={3} data-testid="description-input" /></div>
                
                {/* Photo Upload */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="flex items-center gap-2 mb-3 text-slate-700"><Camera className="w-4 h-4 text-[#2563EB]" />Damage & Repair Photos (Optional)</Label>
                  <p className="text-sm text-slate-600 mb-4">Upload photos of vehicle damage and completed repairs.</p>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#2563EB] hover:bg-blue-50/50 transition-colors">
                    <div className="flex flex-col items-center"><Upload className="w-8 h-8 text-slate-600 mb-2" /><p className="text-sm font-medium text-slate-600">Click to upload photos</p><p className="text-xs text-slate-600 mt-1">PNG, JPG up to 5MB each (max 10 photos)</p></div>
                    <input type="file" className="hidden" accept="image/*" multiple onChange={handlePhotoUpload} data-testid="photo-input" />
                  </label>
                  {damagePhotos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-4">
                      {damagePhotos.map((photo, index) => (
                        <div key={index} className="relative group rounded-lg overflow-hidden border border-slate-200">
                          <img src={photo.preview} alt={`Damage photo ${index + 1}`} className="w-full h-24 object-cover" />
                          <button type="button" onClick={() => removePhoto(index)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Invoice Upload */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="flex items-center gap-2 mb-3 text-slate-700"><Receipt className="w-4 h-4 text-[#2563EB]" />Repair Invoice (Optional)</Label>
                  <p className="text-sm text-slate-600 mb-4">Upload your repair invoice or estimate from the body shop.</p>
                  {!repairInvoice ? (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#2563EB] hover:bg-blue-50/50 transition-colors">
                      <div className="flex flex-col items-center"><FileCheck className="w-8 h-8 text-slate-600 mb-2" /><p className="text-sm font-medium text-slate-600">Click to upload invoice</p><p className="text-xs text-slate-600 mt-1">PDF, PNG, JPG up to 10MB</p></div>
                      <input type="file" className="hidden" accept=".pdf,image/*" onChange={handleInvoiceUpload} data-testid="invoice-input" />
                    </label>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <div className="flex items-center gap-3"><div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><FileCheck className="w-5 h-5 text-emerald-600" /></div><div><p className="font-medium text-emerald-800 truncate max-w-[200px]">{repairInvoice.name}</p><p className="text-xs text-emerald-600">{formatFileSize(repairInvoice.size)}</p></div></div>
                      <button type="button" onClick={() => setRepairInvoice(null)} className="p-2 text-emerald-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4 */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-review">
              <div><h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-2">Review & Submit</h2><p className="text-slate-600">Please review your information before submitting.</p></div>
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4"><h3 className="font-medium text-slate-900 flex items-center gap-2 mb-3"><Car className="w-4 h-4 text-[#2563EB]" />Vehicle Information</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><div className="overflow-hidden"><p className="text-slate-600">VIN</p><p className="font-mono font-medium text-slate-900 break-all">{formData.vin}</p></div>{vinData && <div><p className="text-slate-600">Vehicle</p><p className="font-medium text-slate-900">{vinData.vehicle_profile.year} {vinData.vehicle_profile.make} {vinData.vehicle_profile.model}</p></div>}</div></div>
                <div className="bg-slate-50 rounded-xl p-4"><h3 className="font-medium text-slate-900 flex items-center gap-2 mb-3"><User className="w-4 h-4 text-[#2563EB]" />Policyholder Details</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><div><p className="text-slate-600">Name</p><p className="font-medium text-slate-900">{formData.policyholder_name}</p></div><div className="overflow-hidden"><p className="text-slate-600">Email</p><p className="font-medium text-slate-900 break-all">{formData.policyholder_email}</p></div></div></div>
                <div className="bg-slate-50 rounded-xl p-4"><h3 className="font-medium text-slate-900 flex items-center gap-2 mb-3"><AlertCircle className="w-4 h-4 text-[#2563EB]" />Accident Information</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><div><p className="text-slate-600">Accident Date</p><p className="font-medium text-slate-900">{new Date(formData.accident_date).toLocaleDateString()}</p></div><div><p className="text-slate-600">State</p><p className="font-medium text-slate-900">{formData.state}</p></div><div className="sm:col-span-2"><p className="text-slate-600">Repair Severity</p><p className="font-medium text-slate-900 capitalize">{formData.repair_severity.replace(/_/g, " ")}</p></div></div></div>
                {(damagePhotos.length > 0 || repairInvoice) && (
                  <div className="bg-slate-50 rounded-xl p-4"><h3 className="font-medium text-slate-900 flex items-center gap-2 mb-3"><Upload className="w-4 h-4 text-[#2563EB]" />Uploaded Documents</h3><div className="space-y-2 text-sm">{damagePhotos.length > 0 && <div className="flex items-center gap-2"><Camera className="w-4 h-4 text-slate-600" /><span className="text-slate-600">{damagePhotos.length} damage photo(s)</span><CheckCircle className="w-4 h-4 text-emerald-500" /></div>}{repairInvoice && <div className="flex items-center gap-2"><Receipt className="w-4 h-4 text-slate-600" /><span className="text-slate-600 truncate max-w-[250px]">{repairInvoice.name}</span><CheckCircle className="w-4 h-4 text-emerald-500" /></div>}</div></div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4"><div className="flex gap-3"><Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /><div className="text-sm"><p className="font-medium text-amber-800">Important Information</p><p className="text-amber-700 mt-1">By submitting this claim, you confirm that all information provided is accurate.</p></div></div></div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button variant="outline" onClick={() => { setCurrentStep((prev) => Math.max(1, prev - 1)); window.scrollTo(0, 0); }} disabled={currentStep === 1} className="gap-2 border-slate-200"><ArrowLeft className="w-4 h-4" />Previous</Button>
            {currentStep < 4 ? (
              <Button onClick={() => { setCurrentStep((prev) => Math.min(4, prev + 1)); window.scrollTo(0, 0); }} disabled={!canProceed()} className="bg-[#2563EB] hover:bg-[#1D4ED8] gap-2">Next Step<ArrowRight className="w-4 h-4" /></Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 gap-2">{submitting ? "Submitting..." : "Submit Claim"}<CheckCircle className="w-4 h-4" /></Button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center"><p className="text-slate-600 text-sm">Need help? Call <a href="tel:833-567-7729" className="text-[#2563EB] font-medium hover:underline">833-LOSSPAY</a> or email <a href="mailto:claims@losspay.com" className="text-[#2563EB] font-medium hover:underline">claims@losspay.com</a></p></div>
      </div>
        </>
      )}
    </div>
  );
}
